// typing ref: https://redux.js.org/usage/usage-with-typescript

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { apiRoot } from '../config'
import { clearPreferences, getUserPreferences } from './PreferencesSlice'

export interface LoginPayload {
  email: string
  password: string
}

function updateAxiosToken() {
  const token = localStorage.getItem('token')
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Token ${token}`
  }
}

updateAxiosToken()

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: LoginPayload, { dispatch }) => {
    let response
    try {
      response = await axios.post(apiRoot + '/auth/login/', {
        email,
        password,
      })
    } catch (error) {
      await dispatch(logOut())
      throw error
    }
    localStorage.setItem('token', response.data.key)
    updateAxiosToken()
    await dispatch(fetchUser())
    await dispatch(getUserPreferences())
    return response.data
  },
)

export const logOut = createAsyncThunk('auth/logOut', async (_, { dispatch }) => {
  localStorage.removeItem('token')
  dispatch(clearPreferences())
  updateAxiosToken()
  return null
})

export const fetchUser = createAsyncThunk('auth/fetchUser', async () => {
  if (!localStorage.getItem('token')) throw new Error('No token')
  try {
    const response = await axios.get(apiRoot + '/auth/user/')
    // Map pk to id for compatibility
    const user = response.data
    if (user && user.pk && !user.id) {
      user.id = user.pk
    }
    return user
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      throw new Error('Unauthorized')
    }
    throw err
  }
})

export interface RegisterPayload {
  email: string
  password1: string
  password2: string
}

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (
    { email, password1, password2 }: RegisterPayload,
    { dispatch, rejectWithValue },
  ) => {
    let result
    try {
      const response = await axios.post(apiRoot + '/auth/registration/', {
        username: email,
        email,
        password1,
        password2,
      })
      result = response.data.data
    } catch (err: any) {
      if (!err.response) {
        return rejectWithValue({ message: 'Could not register.' })
      }
      if (err.response.status >= 500) {
        return rejectWithValue({ message: 'Server error' })
      }
      return rejectWithValue(err.response.data)
    }
    const loginResult = await dispatch(login({ email, password: password1 }))
    if (!login.fulfilled.match(loginResult)) {
      return rejectWithValue({ message: 'Could not login.' })
    }
    return result
  },
)

interface User {
  id: number
  username: string
  email: string
}

export interface AuthState {
  user: User | null
  preferences: any | null
  isLoggedIn: boolean
  loading: boolean
  errorMessage: string
}

function getInitialState(): AuthState {
  const token = localStorage.getItem('token')
  return {
    user: null,
    preferences: null,
    isLoggedIn: !!token,
    loading: false,
    errorMessage: '',
  }
}

const initialState: AuthState = getInitialState()

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.user = null
        state.isLoggedIn = false
        state.loading = true
        state.errorMessage = ''
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false
        state.errorMessage = ''
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false
        state.user = null
        const payload: any = action.payload || {}
        if (
          'username' in payload &&
          payload.username[0] === 'A user with that username already exists.'
        ) {
          state.errorMessage = 'UserAlreadyExists'
        } else if (payload.message) {
          state.errorMessage = payload.message
        } else {
          // @ts-ignore
          state.errorMessage = action.error.message
        }
      })
      .addCase(login.pending, (state) => {
        state.loading = true
        state.errorMessage = ''
      })
      .addCase(login.fulfilled, (state) => {
        state.loading = false
        state.isLoggedIn = true
        state.errorMessage = ''
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.user = null
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.errorMessage = 'Invalid credentials'
        } else {
          // @ts-ignore
          state.errorMessage = action.error.message
        }
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload
      })
      .addCase(logOut.fulfilled, (state) => {
        state.isLoggedIn = false
        state.user = null
      })
  },
})

export default authSlice.reducer
